import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Button, AppBar, Toolbar,
  IconButton, CircularProgress, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Stack, Tooltip
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  VpnKey as KeyIcon
} from '@mui/icons-material';
import api from '../../services/api';

const PLAN_OPTIONS = [
  { code: 'basic', name: '創業方案', price: '$6,999/年' },
  { code: 'pro', name: '成長方案', price: '$14,999/年' },
  { code: 'enterprise', name: '品牌方案', price: '$29,999/年' },
];

function PlatformActivations() {
  const navigate = useNavigate();
  const [activations, setActivations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ plan_code: 'basic', quantity: 1, source: 'manual', ezp_order_id: '' });
  const [creating, setCreating] = useState(false);
  const [generatedCodes, setGeneratedCodes] = useState([]);
  const [copySuccess, setCopySuccess] = useState('');

  const token = localStorage.getItem('platform_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchActivations = useCallback(async () => {
    if (!token) { navigate('/platform'); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (statusFilter) params.set('status', statusFilter);
      const res = await api.get(`/platform/activations?${params}`, { headers });
      setActivations(res.data.data.items);
      setPagination(res.data.data.pagination);
    } catch (err) {
      setError('載入失敗');
    } finally {
      setLoading(false);
    }
  }, [token, page, statusFilter]);

  useEffect(() => { fetchActivations(); }, [fetchActivations]);

  const handleCreate = async () => {
    setCreating(true);
    setError('');
    try {
      const res = await api.post('/platform/activations', createForm, { headers });
      setGeneratedCodes(res.data.data);
      setCreateOpen(false);
      fetchActivations();
    } catch (err) {
      setError(err.response?.data?.error || '產生失敗');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('確定要作廢此開通碼？')) return;
    try {
      await api.delete(`/platform/activations/${id}`, { headers });
      fetchActivations();
    } catch (err) {
      setError('刪除失敗');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopySuccess(text);
      setTimeout(() => setCopySuccess(''), 2000);
    });
  };

  const getStatusChip = (row) => {
    if (row.used_at) return <Chip label="已使用" color="success" size="small" />;
    if (new Date(row.expires_at) <= new Date()) return <Chip label="已過期" color="error" size="small" />;
    return <Chip label="可用" color="primary" size="small" />;
  };

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <AppBar position="static" sx={{ bgcolor: '#1a237e' }}>
        <Toolbar>
          <IconButton color="inherit" onClick={() => navigate('/platform/dashboard')} sx={{ mr: 1 }}>
            <BackIcon />
          </IconButton>
          <KeyIcon sx={{ mr: 1 }} />
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
            開通碼管理
          </Typography>
          <Button color="inherit" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            產生開通碼
          </Button>
          <IconButton color="inherit" onClick={fetchActivations}>
            <RefreshIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {/* Generated codes result */}
        {generatedCodes.length > 0 && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setGeneratedCodes([])}>
            <Typography sx={{ fontWeight: 700, mb: 1 }}>已產生 {generatedCodes.length} 組開通碼</Typography>
            {generatedCodes.map((c, i) => (
              <Stack key={i} direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{c.code}</Typography>
                <Tooltip title={copySuccess === c.activation_url ? '已複製' : '複製連結'}>
                  <IconButton size="small" onClick={() => copyToClipboard(c.activation_url)}>
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            ))}
          </Alert>
        )}

        {/* Filter */}
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          {['', 'unused', 'used', 'expired'].map(s => (
            <Chip
              key={s}
              label={s === '' ? '全部' : s === 'unused' ? '可用' : s === 'used' ? '已使用' : '已過期'}
              variant={statusFilter === s ? 'filled' : 'outlined'}
              color={statusFilter === s ? 'primary' : 'default'}
              onClick={() => { setStatusFilter(s); setPage(1); }}
            />
          ))}
        </Stack>

        <TableContainer component={Paper}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>開通碼</TableCell>
                  <TableCell>方案</TableCell>
                  <TableCell>月數</TableCell>
                  <TableCell>來源</TableCell>
                  <TableCell>狀態</TableCell>
                  <TableCell>使用租戶</TableCell>
                  <TableCell>建立時間</TableCell>
                  <TableCell>過期時間</TableCell>
                  <TableCell>操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activations.map(row => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {row.code}
                        </Typography>
                        <Tooltip title={copySuccess === row.code ? '已複製' : '複製'}>
                          <IconButton size="small" onClick={() => copyToClipboard(row.code)}>
                            <CopyIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                    <TableCell>{row.plan_name || row.plan_code}</TableCell>
                    <TableCell>{row.duration_months}</TableCell>
                    <TableCell>{row.source}</TableCell>
                    <TableCell>{getStatusChip(row)}</TableCell>
                    <TableCell>
                      {row.tenant_name ? `${row.tenant_name} (${row.tenant_slug})` : '-'}
                    </TableCell>
                    <TableCell>{new Date(row.created_at).toLocaleDateString('zh-TW')}</TableCell>
                    <TableCell>{new Date(row.expires_at).toLocaleDateString('zh-TW')}</TableCell>
                    <TableCell>
                      {!row.used_at && (
                        <>
                          <Tooltip title="複製開通連結">
                            <IconButton size="small" onClick={() => copyToClipboard(`https://urstore.tw/apply?activation=${row.code}`)}>
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="作廢">
                            <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {activations.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">尚無開通碼</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {pagination.pages > 1 && (
          <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
            <Button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>上一頁</Button>
            <Typography sx={{ lineHeight: '36px' }}>{page} / {pagination.pages}</Typography>
            <Button disabled={page >= pagination.pages} onClick={() => setPage(p => p + 1)}>下一頁</Button>
          </Stack>
        )}
      </Container>

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>產生開通碼</DialogTitle>
        <DialogContent>
          <TextField
            select fullWidth label="方案" value={createForm.plan_code} sx={{ mt: 1, mb: 2 }}
            onChange={e => setCreateForm(f => ({ ...f, plan_code: e.target.value }))}
          >
            {PLAN_OPTIONS.map(p => (
              <MenuItem key={p.code} value={p.code}>{p.name} ({p.price})</MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth label="數量" type="number" value={createForm.quantity} sx={{ mb: 2 }}
            onChange={e => setCreateForm(f => ({ ...f, quantity: Math.min(50, Math.max(1, parseInt(e.target.value) || 1)) }))}
            inputProps={{ min: 1, max: 50 }}
            helperText="一次最多 50 組"
          />
          <TextField
            select fullWidth label="來源" value={createForm.source} sx={{ mb: 2 }}
            onChange={e => setCreateForm(f => ({ ...f, source: e.target.value }))}
          >
            <MenuItem value="manual">手動產生</MenuItem>
            <MenuItem value="ezp">EZP 購買</MenuItem>
          </TextField>
          {createForm.source === 'ezp' && (
            <TextField
              fullWidth label="EZP 訂單號" value={createForm.ezp_order_id}
              onChange={e => setCreateForm(f => ({ ...f, ezp_order_id: e.target.value }))}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>取消</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating}>
            {creating ? <CircularProgress size={20} /> : '產生'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PlatformActivations;
